<template>
	<div>
		<h6 class="module-title">{{ lang.nd_job_cards }}</h6>

		<v-card flat class="section-card mb-3">
			<div class="section-head">
				<h6 class="section-title mb-0">{{ lang.nd_status || 'Status' }}</h6>
			</div>

			<div class="status-summary-row">
				<v-card
					v-for="statusItem in jobCardStatusSummary"
					:key="`status_${statusItem.value}`"
					flat
					class="status-summary-card"
					:class="{ 'status-summary-card--inactive': !isStatusSelected(statusItem.value) }"
					:style="{ backgroundColor: statusItem.bgColor, color: statusItem.color }"
					@click="toggleStatusSelection(statusItem.value)"
				>
					<div class="status-summary-name">
						<v-icon small class="status-check-icon">
							{{ isStatusSelected(statusItem.value) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
						</v-icon>
						{{ statusItem.name }}
					</div>
					<div class="status-summary-total">{{ statusItem.total }}</div>
				</v-card>
			</div>
		</v-card>

		<v-card flat class="section-card mb-3">
			<div class="section-head">
				<h6 class="section-title mb-0">{{ lang.nd_job_orders || 'Job Orders' }}</h6>
			</div>

			<div class="job-order-kpi-line" :style="{ '--job-order-kpi-count': Math.max(jobOrderKpiCards.length, 1) }">
				<v-card
					v-for="card in jobOrderKpiCards"
					:key="card.key"
					flat
					class="job-order-kpi-card"
					:style="{ backgroundColor: card.bgColor, color: card.color, flex: `1 1 calc(100% / ${Math.max(jobOrderKpiCards.length, 1)})`, minWidth: '180px' }"
				>
					<div class="job-order-kpi-label">{{ card.label }}</div>
					<div class="job-order-kpi-value">{{ formatCurrency(card.value) }}</div>
				</v-card>
			</div>
		</v-card>

		<v-card flat class="section-card mb-3">
			<div class="section-head">
				<h6 class="section-title mb-0">{{ lang.nd_employee_labor_performance }}</h6>
				<v-chip small outlined color="primary">{{ laborPerformanceCards.length }} {{ lang.nd_employees }}</v-chip>
			</div>

			<div class="labor-scroll-row">
				<v-card
					v-for="labor in laborPerformanceCards"
					:key="labor.employeeId"
					outlined
					class="labor-card"
				>
					<div class="d-flex justify-content-between align-items-start mb-2">
						<div>
							<div class="labor-name">{{ labor.employeeName }}</div>
							<div class="labor-branch">{{ labor.branchName }}</div>
						</div>
						<div class="labor-ratio">{{ labor.efficiencyPercent }}%</div>
					</div>
					<div class="labor-metrics">
						<span>{{ lang.nd_working }}: {{ labor.totalWorkingHours.toFixed(1) }}h</span>
						<span>{{ lang.nd_late }}: {{ labor.totalLateHours.toFixed(1) }}h</span>
					</div>
					<v-progress-linear
						:value="labor.efficiencyPercent"
						height="7"
						color="primary"
						background-color="grey lighten-3"
						rounded
					/>
				</v-card>
			</div>
		</v-card>

		<b-row>
			<b-col
				v-for="jobCard in filteredJobCardsWithBranch"
				:key="jobCard.id"
				cols="12"
				md="6"
				xl="3"
				class="mb-3"
			>
				<v-card class="job-card h-100" outlined>
					<div
						class="status-strip"
						:class="statusClass(jobCard.jobStatus)"
					>
						<v-chip
							small
							dark
							:color="statusColor(jobCard.jobStatus)"
						>
							{{ statusLabel(jobCard.jobStatus) }}
						</v-chip>
					</div>

					<v-card-text class="py-3 px-3">
						<div class="card-header-block mb-2">
							<div class="card-meta-row">
								<v-chip x-small outlined class="meta-chip meta-chip--id" color="indigo">
									{{ lang.nd_card_id }}: {{ jobCard.cardId }}
								</v-chip>
								<v-chip x-small outlined class="meta-chip meta-chip--received" color="teal">
									{{ lang.nd_received_date }}: {{ formatShortDate(jobCard.receivedDate) }}
								</v-chip>
								<v-chip x-small outlined class="meta-chip meta-chip--expected" color="orange darken-2">
									{{ lang.nd_expected_date }}: {{ formatShortDate(jobCard.deliveryDate) }}
								</v-chip>
								<v-chip
									v-if="Number(jobCard.lateDays || 0) > 0"
									x-small
									dark
									class="meta-chip meta-chip--late"
									color="red darken-2"
								>
									{{ lang.nd_late }}: {{ Number(jobCard.lateDays || 0) }}d
								</v-chip>
							</div>
							<div class="plate-no">{{ jobCard.carPlateNumber }}</div>
							<div class="car-model">{{ jobCard.carModel }} {{ jobCard.carYear }}</div>
							<div class="branch-name">{{ lang.nd_branch }}: {{ jobCard.branchName }}</div>
							<div class="customer-info">
								{{ jobCard.customerName }} | {{ jobCard.customerMobile }}
							</div>
						</div>

						<div class="financial-grid">
							<div class="metric-item">
								<span>{{ lang.nd_total_bill }}</span>
								<strong>{{ formatCurrency(jobCard.totalBillAmount) }}</strong>
							</div>
							<div class="metric-item">
								<span>{{ lang.nd_paid }}</span>
								<strong class="paid-text">{{ formatCurrency(jobCard.paidAmount) }}</strong>
							</div>
							<div class="metric-item">
								<span>{{ lang.nd_remaining }}</span>
								<strong class="remaining-text">{{ formatCurrency(jobCard.remainingBalance) }}</strong>
							</div>
							<div class="metric-item">
								<span>{{ lang.nd_vat }}</span>
								<strong>{{ formatCurrency(jobCard.vatAmount) }}</strong>
							</div>
							<div class="metric-item">
								<span>{{ lang.nd_total_expenses }}</span>
								<strong>{{ formatCurrency(jobCard.totalExpenses) }}</strong>
							</div>
							<div class="metric-item">
								<span>{{ lang.nd_profit }}</span>
								<strong :class="jobCard.calculatedProfit >= 0 ? 'profit-text' : 'loss-text'">
									{{ formatCurrency(jobCard.calculatedProfit) }}
								</strong>
							</div>
						</div>
					</v-card-text>

					<v-divider />
					<v-card-actions class="px-3 py-2">
						<v-btn
							small
							color="primary"
							text
							@click="openJobCardDetails(jobCard)"
						>
							{{ lang.nd_view_details }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</b-col>
		</b-row>

		<v-dialog v-model="detailsDialog" max-width="720">
			<v-card>
				<v-card-title class="headline">{{ lang.nd_job_card_details }}</v-card-title>
				<v-card-text v-if="selectedJobCard" class="pb-2">
					<b-row>
						<b-col cols="12" md="6">
							<div class="detail-item"><strong>{{ lang.nd_plate }}:</strong> {{ selectedJobCard.carPlateNumber }}</div>
							<div class="detail-item"><strong>{{ lang.nd_vehicle }}:</strong> {{ selectedJobCard.carModel }} {{ selectedJobCard.carYear }}</div>
							<div class="detail-item"><strong>{{ lang.nd_branch }}:</strong> {{ selectedJobCard.branchName }}</div>
							<div class="detail-item"><strong>{{ lang.nd_customer }}:</strong> {{ selectedJobCard.customerName }}</div>
							<div class="detail-item"><strong>{{ lang.nd_mobile }}:</strong> {{ selectedJobCard.customerMobile }}</div>
						</b-col>
						<b-col cols="12" md="6">
							<div class="detail-item"><strong>{{ lang.nd_status }}:</strong> {{ statusLabel(selectedJobCard.jobStatus) }}</div>
							<div class="detail-item"><strong>{{ lang.nd_total_bill }}:</strong> {{ formatCurrency(selectedJobCard.totalBillAmount) }}</div>
							<div class="detail-item"><strong>{{ lang.nd_paid }}:</strong> {{ formatCurrency(selectedJobCard.paidAmount) }}</div>
							<div class="detail-item"><strong>{{ lang.nd_remaining }}:</strong> {{ formatCurrency(selectedJobCard.remainingBalance) }}</div>
						</b-col>
					</b-row>
				</v-card-text>
				<v-card-actions class="justify-end">
					<v-btn text color="grey darken-1" @click="detailsDialog = false">{{ lang.close }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import axios from 'axios';

export default {
	name: 'JobCardModuleContent',
	props: {
		filters: {
			type: Object,
			required: true
		},
		branchOptions: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			detailsDialog: false,
			selectedJobCard: null,
			jobCardsLoading: false,
			apiJobCards: [],
			apiJobCardStatusSummary: [],
			apiJobOrderRowTypeSummary: [],
			apiJobOrderTotals: {
				sales: 0,
				expenses: 0,
				extraProfit: 0,
				totalProfit: 0
			},
			selectedStatusValues: [],
			statusSelectionInitialized: false,
			knownStatusValues: [],
			apiLaborPerformanceCards: []
		};
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.fetchJobCardsData();
			}
		}
	},
	mounted() {
		this.fetchJobCardsData();
	},
	computed: {
		lang: function () {
			if (this.$cookies.get(this.$store.state.LangCooki) != null) {
				if (this.$cookies.get(this.$store.state.LangCooki) == 'en') {
					return this.$store.state.lang.en;
				} else if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') {
					return this.$store.state.lang.ur;
				} else {
					return this.$store.state.lang.ar;
				}
			}
			else if (this.$store.state.deflang == 'en') {
				return this.$store.state.lang.en;
			}
			else if (this.$store.state.deflang == 'ur') {
				return this.$store.state.lang.ur;
			} else {
				return this.$store.state.lang.ar;
			}
		},
		selectedBranchIds() {
			return this.filters.branches && this.filters.branches.length
				? this.filters.branches
				: this.branchOptions.map((branch) => branch.id);
		},
		normalizedDateRange() {
			if (!this.filters.startDate && !this.filters.endDate) {
				// Midnight, like the server's 00:00:00 bound, so cards on the first day are not dropped.
				const now = new Date();
				const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				const start = new Date(end);
				start.setDate(end.getDate() - 30);
				return { start, end };
			}

			const now = new Date();
			const end = this.parseISODate(this.filters.endDate) || now;
			const start = this.parseISODate(this.filters.startDate) || new Date(end.getFullYear(), end.getMonth(), 1);
			return start <= end ? { start, end } : { start: end, end: start };
		},
		filteredJobCards() {
			const { start, end } = this.normalizedDateRange;
			return this.apiJobCards.filter((card) => {
				const cardDate = this.parseISODate(card.jobDate);
				const inBranch = !this.selectedBranchIds.length || this.selectedBranchIds.includes(card.branchId);
				const inDate = cardDate && cardDate >= start && cardDate <= end;
				const statusValue = Number(card.statusCode || 0);
				const inSelectedStatus = this.selectedStatusValues.includes(statusValue);
				return inBranch && inDate && inSelectedStatus;
			});
		},
		filteredJobCardsWithBranch() {
			return this.filteredJobCards.map((card) => {
				const branch = this.branchOptions.find((opt) => opt.id === card.branchId);
				return {
					...card,
					branchName: branch ? branch.name : (card.branchName || this.lang.nd_unknown_branch)
				};
			});
		},
		laborPerformanceCards() {
			return this.apiLaborPerformanceCards.filter((item) => {
				return !this.selectedBranchIds.length || this.selectedBranchIds.includes(item.branchId);
			});
		},
		jobCardStatusSummary() {
			return this.apiJobCardStatusSummary;
		},
		jobOrderKpiCards() {
			const rowTypeMap = {};
			this.apiJobOrderRowTypeSummary.forEach((row) => {
				rowTypeMap[Number(row.value || 0)] = row;
			});

			const profitPositive = Number(this.apiJobOrderTotals.totalProfit || 0) >= 0;
			const type2 = Number((rowTypeMap[2] && rowTypeMap[2].total) || 0);
			const type4 = Number((rowTypeMap[4] && rowTypeMap[4].total) || 0);
			const type5 = Number((rowTypeMap[5] && rowTypeMap[5].total) || 0);
			return [
				{
					key: 'sales',
					label: rowTypeMap[1] ? rowTypeMap[1].label : (this.lang.nd_sales_items || 'Sales Items'),
					value: Number(this.apiJobOrderTotals.sales || 0),
					bgColor: (rowTypeMap[1] && rowTypeMap[1].bgColor) ? rowTypeMap[1].bgColor : '#2563eb',
					color: (rowTypeMap[1] && rowTypeMap[1].color) ? rowTypeMap[1].color : '#ffffff'
				},
				{
					key: 'expense_2',
					label: rowTypeMap[2] ? rowTypeMap[2].label : 'Type 2',
					value: type2,
					bgColor: (rowTypeMap[2] && rowTypeMap[2].bgColor) ? rowTypeMap[2].bgColor : '#dc2626',
					color: (rowTypeMap[2] && rowTypeMap[2].color) ? rowTypeMap[2].color : '#ffffff'
				},
				{
					key: 'expense_4',
					label: rowTypeMap[4] ? rowTypeMap[4].label : 'Type 4',
					value: type4,
					bgColor: (rowTypeMap[4] && rowTypeMap[4].bgColor) ? rowTypeMap[4].bgColor : '#b91c1c',
					color: (rowTypeMap[4] && rowTypeMap[4].color) ? rowTypeMap[4].color : '#ffffff'
				},
				{
					key: 'expense_5',
					label: rowTypeMap[5] ? rowTypeMap[5].label : 'Type 5',
					value: type5,
					bgColor: (rowTypeMap[5] && rowTypeMap[5].bgColor) ? rowTypeMap[5].bgColor : '#991b1b',
					color: (rowTypeMap[5] && rowTypeMap[5].color) ? rowTypeMap[5].color : '#ffffff'
				},
				{
					key: 'extraProfit',
					label: rowTypeMap[7] ? rowTypeMap[7].label : (this.lang.nd_extra_profit || 'Extra Profit'),
					value: Number(this.apiJobOrderTotals.extraProfit || 0),
					bgColor: (rowTypeMap[7] && rowTypeMap[7].bgColor) ? rowTypeMap[7].bgColor : '#0f766e',
					color: (rowTypeMap[7] && rowTypeMap[7].color) ? rowTypeMap[7].color : '#ffffff'
				},
				{
					key: 'profit',
					label: this.lang.nd_total_profit || 'Total Profit',
					value: Number(this.apiJobOrderTotals.totalProfit || 0),
					bgColor: profitPositive ? '#16a34a' : '#dc2626',
					color: '#ffffff'
				}
			];
		}
	},
	methods: {
		// toISOString() converts to UTC, which moved every date one day back for users east of UTC.
		toLocalISODate(date) {
			const pad = (n) => String(n).padStart(2, '0');
			return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
		},
		toggleStatusSelection(statusValue) {
			const value = Number(statusValue || 0);
			if (value < 0) return;

			if (this.selectedStatusValues.includes(value)) {
				this.selectedStatusValues = this.selectedStatusValues.filter((item) => item !== value);
				return;
			}

			this.selectedStatusValues = [...this.selectedStatusValues, value];
		},
		isStatusSelected(statusValue) {
			return this.selectedStatusValues.includes(Number(statusValue || 0));
		},
		dashboardApiPath() {
			const base =
				this.$store &&
				this.$store.state &&
				this.$store.state.SAMCOTEC &&
				this.$store.state.SAMCOTEC.r_path
					? this.$store.state.SAMCOTEC.r_path
					: '';
			if (base && base.indexOf('index.php') !== -1) {
				return base.replace('index.php', 'new-dashboard.php');
			}
			if (base && base.endsWith('/')) {
				return `${base}new-dashboard.php`;
			}
			return 'api/new-dashboard.php';
		},
		fetchJobCardsData() {
			const post = new FormData();
			post.append('module', 'job_cards');
			post.append('startDate', this.toLocalISODate(this.normalizedDateRange.start));
			post.append('endDate', this.toLocalISODate(this.normalizedDateRange.end));
			post.append('branchIds', this.selectedBranchIds.join(','));
			post.append('lang', this.lang.langname || 'ar');

			this.jobCardsLoading = true;

			return axios.post(this.dashboardApiPath(), post)
				.then((response) => {
					const payload = response && response.data ? response.data : {};
					if (payload.status !== 1 || !payload.data) {
						this.apiJobCards = [];
						this.apiJobCardStatusSummary = [];
						this.apiJobOrderRowTypeSummary = [];
						this.apiJobOrderTotals = { sales: 0, expenses: 0, extraProfit: 0, totalProfit: 0 };
						this.apiLaborPerformanceCards = [];
						return;
					}

					const cards = Array.isArray(payload.data.jobCards) ? payload.data.jobCards : [];
					const statusSummary = Array.isArray(payload.data.jobCardStatusSummary) ? payload.data.jobCardStatusSummary : [];
					const rowTypeSummary = Array.isArray(payload.data.jobOrderRowTypeSummary) ? payload.data.jobOrderRowTypeSummary : [];
					const jobOrderTotals = payload.data.jobOrderTotals && typeof payload.data.jobOrderTotals === 'object'
						? payload.data.jobOrderTotals
						: { sales: 0, expenses: 0, extraProfit: 0, totalProfit: 0 };
					const labor = Array.isArray(payload.data.laborPerformanceCards) ? payload.data.laborPerformanceCards : [];

					this.apiJobCardStatusSummary = statusSummary.map((item) => ({
						value: Number(item.value || 0),
						name: item.name || this.lang.nd_unknown,
						bgColor: item.bgColor || '#eef2f7',
						color: item.color || '#334155',
						total: Number(item.total || 0)
					}));

					this.apiJobOrderRowTypeSummary = rowTypeSummary.map((item) => ({
						value: Number(item.value || 0),
						label: item.label || this.lang.nd_unknown,
						bgColor: item.bgColor || '',
						color: item.color || '',
						total: Number(item.total || 0)
					}));
					this.apiJobOrderTotals = {
						sales: Number(jobOrderTotals.sales || 0),
						expenses: Number(jobOrderTotals.expenses || 0),
						extraProfit: Number(jobOrderTotals.extraProfit || 0),
						totalProfit: Number(jobOrderTotals.totalProfit || 0)
					};

					const availableStatusValues = this.apiJobCardStatusSummary.map((item) => Number(item.value || 0));
					if (!this.statusSelectionInitialized) {
						this.selectedStatusValues = [...availableStatusValues];
						this.statusSelectionInitialized = true;
					} else {
						// Keep the user's choices, but show statuses that were not offered before
						// (otherwise their cards were hidden without any toggle being off).
						const newValues = availableStatusValues.filter((value) => !this.knownStatusValues.includes(value));
						this.selectedStatusValues = this.selectedStatusValues
							.filter((value) => availableStatusValues.includes(value))
							.concat(newValues);
					}
					this.knownStatusValues = availableStatusValues;

					this.apiJobCards = cards.map((card) => {
						const totalBillAmount = Number(card.totalBillAmount || card.total_bill || 0);
						const paidAmount = Number(card.paidAmount || card.paid || 0);
						const remainingBalance = card.remainingBalance !== undefined
							? Number(card.remainingBalance || 0)
							: Number(totalBillAmount - paidAmount);
						const totalExpenses = Number(card.totalExpenses || card.total_expenses || 0);
						const calculatedProfit = card.calculatedProfit !== undefined
							? Number(card.calculatedProfit || 0)
							: Number(totalBillAmount - totalExpenses);

						return {
							id: Number(card.id || 0),
							jobDate: card.jobDate || card.date_in || '',
							receivedDate: card.receivedDate || card.received_date || card.jobDate || card.date_in || '',
							deliveryDate: card.deliveryDate || card.delivery_date || '',
							cardId: Number(card.cardId || card.card_id || card.id || 0),
							lateDays: Number(card.lateDays || card.late_days || 0),
							statusCode: Number(card.statusCode || card.status_code || 0),
							branchId: Number(card.branchId || card.branchid || 0),
							jobStatus: card.jobStatus || card.status || 'under_process',
							carPlateNumber: card.carPlateNumber || card.plate_number || '-',
							carModel: card.carModel || card.car_model || '-',
							carYear: card.carYear || card.car_year || '-',
							customerName: card.customerName || card.customer || this.lang.nd_unknown,
							customerMobile: card.customerMobile || card.mobile || '-',
							totalBillAmount,
							paidAmount,
							remainingBalance,
							vatAmount: Number(card.vatAmount || card.vat || 0),
							totalExpenses,
							calculatedProfit
						};
					});

					this.apiLaborPerformanceCards = labor.map((item) => {
						const working = Number(item.totalWorkingHours || item.total_working_hours || 0);
						const late = Number(item.totalLateHours || item.total_late_hours || 0);
						const total = working + late;
						const efficiencyPercent = item.efficiencyPercent !== undefined
							? Number(item.efficiencyPercent || 0)
							: (total > 0 ? Math.round((working / total) * 100) : 0);

						return {
							employeeId: Number(item.employeeId || item.employee_id || 0),
							employeeName: item.employeeName || item.employee_name || this.lang.nd_unknown,
							branchId: Number(item.branchId || item.branchid || 0),
							branchName: item.branchName || item.branch || this.lang.nd_unknown_branch,
							totalWorkingHours: working,
							totalLateHours: late,
							efficiencyPercent
						};
					});
				})
				.catch(() => {
					this.apiJobCards = [];
					this.apiJobCardStatusSummary = [];
					this.apiJobOrderRowTypeSummary = [];
					this.apiJobOrderTotals = { sales: 0, expenses: 0, extraProfit: 0, totalProfit: 0 };
					this.selectedStatusValues = [];
					this.knownStatusValues = [];
					this.statusSelectionInitialized = false;
					this.apiLaborPerformanceCards = [];
				})
				.finally(() => {
					this.jobCardsLoading = false;
				});
		},
		parseISODate(value) {
			if (!value) return null;
			const [year, month, day] = value.split('-').map(Number);
			if (!year || !month || !day) return null;
			return new Date(year, month - 1, day);
		},
		statusLabel(status) {
			const map = {
				under_process: this.lang.nd_under_process,
				late: this.lang.nd_late,
				ready_to_collect: this.lang.nd_ready_to_collect,
				closed: this.lang.nd_closed,
				need_spare_part: this.lang.nd_need_spare_part || 'Need Spare Part',
				checking: this.lang.nd_checking || 'Checking',
				canceled: this.lang.nd_canceled || 'Canceled'
			};
			return map[status] || this.lang.nd_unknown;
		},
		statusColor(status) {
			const map = {
				under_process: 'blue',
				late: 'red darken-2',
				ready_to_collect: 'light-green',
				closed: 'green darken-3',
				need_spare_part: 'deep-orange darken-4',
				checking: 'teal darken-3',
				canceled: 'grey darken-2'
			};
			return map[status] || 'grey';
		},
		statusClass(status) {
			const map = {
				under_process: 'status-under-process',
				late: 'status-late',
				ready_to_collect: 'status-ready',
				closed: 'status-closed',
				need_spare_part: 'status-need-spare-part',
				checking: 'status-checking',
				canceled: 'status-default'
			};
			return map[status] || 'status-default';
		},
		openJobCardDetails(jobCard) {
			this.selectedJobCard = jobCard;
			this.detailsDialog = true;
		},
		formatShortDate(value) {
			if (!value) return '-';
			const normalized = String(value).slice(0, 10);
			const dt = this.parseISODate(normalized);
			if (!dt) return normalized || '-';
			return dt.toLocaleDateString(this.currencyLocale());
		},
		currencyLocale() {
			if (this.lang && this.lang.langname === 'ar') return 'ar-SA';
			if (this.lang && this.lang.langname === 'ur') return 'ur-PK';
			return 'en-US';
		},
		formatCurrency(value) {
			const amount = Number(value || 0);
			return new Intl.NumberFormat(this.currencyLocale(), {
				style: 'currency',
				currency: 'SAR',
				maximumFractionDigits: 2
			}).format(amount);
		}
	}
};
</script>

<style scoped>
.module-title {
	margin-bottom: 8px;
	font-size: 1rem;
	font-weight: 700;
	color: #2d3748;
}

.section-card {
	border: 1px solid #e6ecf3;
	border-radius: 10px;
	padding: 14px;
	background: #ffffff;
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	margin-bottom: 12px;
}

.section-title {
	font-size: 0.95rem;
	font-weight: 700;
	color: #2d3748;
}

.status-summary-row {
	display: flex;
	flex-wrap: nowrap;
	gap: 10px;
	overflow-x: auto;
	padding-bottom: 4px;
	justify-content: center;
}

.status-summary-card {
	min-width: 160px;
	padding: 10px 12px;
	border-radius: 10px;
	border: 1px solid rgba(15, 23, 42, 0.08);
	display: flex;
	align-items: center;
	justify-content: space-between;
	cursor: pointer;
}

.status-summary-card--inactive {
	opacity: 0.55;
}

.status-summary-name {
	font-size: 0.82rem;
	font-weight: 700;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.status-check-icon {
	font-size: 0.95rem !important;
}

.status-summary-total {
	font-size: 1.05rem;
	font-weight: 800;
}

.job-order-kpi-card {
	padding: 12px;
	border-radius: 10px;
	min-height: 88px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
}

.job-order-kpi-line {
	display: flex;
	flex-wrap: nowrap;
	gap: 10px;
	overflow-x: auto;
	padding-bottom: 4px;
	justify-content: center;
}

.job-order-kpi-label {
	font-size: 0.8rem;
	font-weight: 700;
	opacity: 0.95;
}

.job-order-kpi-value {
	font-size: 1rem;
	font-weight: 800;
}

.labor-scroll-row {
	display: flex;
	overflow-x: auto;
	gap: 10px;
	padding-bottom: 4px;
}

.labor-card {
	min-width: 240px;
	border-color: #dfe7f1 !important;
	padding: 10px;
	border-radius: 8px;
	background: #fcfdff;
}

.labor-name {
	font-size: 0.86rem;
	font-weight: 700;
	color: #1f2937;
}

.labor-branch {
	font-size: 0.76rem;
	color: #6b7280;
}

.labor-ratio {
	font-size: 0.9rem;
	font-weight: 800;
	color: #1d4ed8;
}

.labor-metrics {
	display: flex;
	justify-content: space-between;
	font-size: 0.76rem;
	color: #475569;
	margin-bottom: 8px;
}

.job-card {
	border-color: #dfe7f1 !important;
	border-radius: 10px;
}

.status-strip {
	padding: 8px 10px;
	border-top-left-radius: 10px;
	border-top-right-radius: 10px;
}

.status-under-process {
	background: #e7f0ff;
}

.status-late {
	background: #fde8e8;
}

.status-ready {
	background: #ecfdf3;
}

.status-closed {
	background: #dff7e5;
}

.status-need-spare-part {
	background: #fff1e8;
}

.status-checking {
	background: #e6fffa;
}

.status-default {
	background: #eef2f7;
}

.card-header-block {
	line-height: 1.3;
}

.card-meta-row {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 4px;
}

.meta-chip {
	font-size: 0.68rem !important;
	font-weight: 700;
	height: 20px !important;
}

.plate-no {
	font-size: 0.94rem;
	font-weight: 800;
	color: #111827;
}

.car-model {
	font-size: 0.82rem;
	font-weight: 600;
	color: #334155;
}

.branch-name {
	font-size: 0.76rem;
	font-weight: 600;
	color: #2563eb;
}

.customer-info {
	font-size: 0.76rem;
	color: #64748b;
}

.financial-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px 10px;
}

.metric-item {
	display: flex;
	flex-direction: column;
	font-size: 0.74rem;
	color: #64748b;
}

.metric-item strong {
	font-size: 0.83rem;
	color: #111827;
	font-weight: 700;
}

.paid-text {
	color: #166534 !important;
}

.remaining-text {
	color: #b91c1c !important;
}

.profit-text {
	color: #166534 !important;
}

.loss-text {
	color: #7f1d1d !important;
}

.detail-item {
	font-size: 0.85rem;
	color: #334155;
	margin-bottom: 8px;
}

@media (max-width: 767px) {
	.financial-grid {
		grid-template-columns: 1fr;
	}
}
</style>
